import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, GoldButton } from "@/components/admin/AdminLayout";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useLang } from "@/lib/lang";
import { ChevronDown, Info, Percent } from "lucide-react";

/**
 * Iter 26 (Msg 673) — Fee configuration simplified.
 *
 * Only 4 essentials are always visible:
 *   1. Platform fee per vehicle category
 *   2. IfthenPay passthrough toggle + rates (optional)
 *   3. Hotel default commission %
 *   4. Fleet default commission %
 *
 * Everything else lives inside an "Advanced" collapsible.
 */

const ADVANCED_GROUPS = [
  {
    title: "Regras de preço",
    fields: [
      { k: "distance_buffer_pct", l: "Distance buffer (%)", type: "number", step: "0.1" },
      { k: "minimum_fee", l: "Fee mínimo plataforma (€)", type: "number", step: "0.1" },
      { k: "minimum_booking_eur", l: "Reserva mínima (€)", type: "number", step: "0.5" },
    ],
  },
  {
    title: "Cancelamentos & booking",
    fields: [
      { k: "cancellation_penalty_pct", l: "Penalização (%)", type: "number", step: "0.1" },
      { k: "cancellation_window_hours", l: "Janela cancelamento (h)", type: "number" },
      { k: "min_advance_hours", l: "Antecedência mínima (h)", type: "number" },
    ],
  },
  {
    title: "Buffers auto-dispatch (min)",
    fields: [
      { k: "buffer_short_min", l: "Tour curto (≤ 3h)", type: "number" },
      { k: "buffer_medium_min", l: "Tour médio (3–6h)", type: "number" },
      { k: "buffer_long_min", l: "Tour longo (> 6h)", type: "number" },
      { k: "buffer_transfer_min", l: "Transfer", type: "number" },
      { k: "min_gap_min", l: "Gap mínimo entre reservas", type: "number" },
    ],
  },
];

export default function FeeConfig() {
  const { t } = useLang();
  const [cfg, setCfg] = useState(null);
  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [multipliers, setMultipliers] = useState([]);
  const [savingMult, setSavingMult] = useState(false);

  useEffect(() => {
    api.get("/admin/fee-config").then((r) => setCfg(r.data));
    api.get("/public/vehicle-categories").then((r) => setCategories(r.data || [])).catch(() => {});
    api.get("/admin/fee-config/partner-multipliers").then((r) => setMultipliers(r.data || [])).catch(() => {});
  }, []);

  if (!cfg) return <div className="p-10 text-slate-500">Loading…</div>;

  const feesByCat = cfg.platform_fee_by_category || {};
  const setCatFee = (key, val) => setCfg({
    ...cfg,
    platform_fee_by_category: { ...feesByCat, [key]: Number(val) || 0 },
  });

  const setMultRow = (idx, field, value) => {
    setMultipliers((prev) => prev.map((row, i) => (i === idx ? { ...row, [field]: Number(value) || 0 } : row)));
  };
  const saveMultipliers = async () => {
    setSavingMult(true);
    try {
      const clean = multipliers.map((r) => ({ pct: Number(r.pct), multiplier: Number(r.multiplier) }));
      const { data } = await api.put("/admin/fee-config/partner-multipliers", clean);
      setMultipliers(data);
      toast.success("Tabela de comissões guardada");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingMult(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/fee-config", cfg);
      toast.success(t("fees_saved_toast"));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="p-10 max-w-[1200px]">
      <PageHeader
        eyebrow="Definições · Fees"
        title="Configuração de taxas"
        description="Configure apenas o essencial. Cada categoria de veículo tem a sua platform fee, e os defaults para hotéis e frotas aplicam-se automaticamente."
      >
        <GoldButton onClick={save} disabled={saving} data-testid="save-fees">
          {saving ? t("action_saving") : t("fees_save_config")}
        </GoldButton>
      </PageHeader>

      {/* Essentials */}
      <div className="space-y-6">
        {/* 1. Platform fee per category */}
        <div className="border-2 border-[#f59e0b]/40 bg-amber-50/30 p-6" data-testid="fee-per-category-block">
          <div className="flex items-start justify-between mb-4">
            <div>
              <p className="label-caps text-[#f59e0b]">Platform fee · por categoria</p>
              <p className="text-xs text-slate-600 mt-1">Percentagem que a DiTours retém em cada reserva, definida por categoria de veículo.</p>
            </div>
          </div>
          {categories.length === 0 ? (
            <div className="text-sm text-slate-500">A carregar categorias…</div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {categories.map((c) => (
                <div key={c.key}>
                  <label className="label-caps block mb-1.5">{c.label}</label>
                  <div className="relative">
                    <Input
                      type="number"
                      step="0.1"
                      value={feesByCat[c.key] ?? cfg.platform_fee_pct ?? 15}
                      onChange={(e) => setCatFee(c.key, e.target.value)}
                      className="input-dt pr-8"
                      data-testid={`fee-cat-${c.key}`}
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-xs pointer-events-none">%</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 2. Hotel + Fleet defaults */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="border border-slate-200 bg-white p-6" data-testid="fee-hotel-block">
            <p className="label-caps mb-2">Hotel / Parceiro · fee por defeito</p>
            <p className="text-xs text-slate-600 mb-4">Comissão da DiTours sobre o preço vendido pelo hotel/parceiro. Cada hotel pode sobrescrever esta % na sua ficha.</p>
            <div className="relative">
              <Input
                type="number"
                step="0.1"
                value={cfg.hotel_default_fee_pct ?? 15}
                onChange={(e) => setCfg({ ...cfg, hotel_default_fee_pct: Number(e.target.value) })}
                className="input-dt pr-8"
                data-testid="fee-hotel-default"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-xs pointer-events-none">%</span>
            </div>
          </div>

          <div className="border border-slate-200 bg-white p-6" data-testid="fee-fleet-block">
            <p className="label-caps mb-2">Frota · fee por defeito</p>
            <p className="text-xs text-slate-600 mb-4">Comissão da DiTours sobre o payout do motorista quando este pertence a uma frota. Cada frota pode sobrescrever na sua ficha.</p>
            <div className="relative">
              <Input
                type="number"
                step="0.1"
                value={cfg.fleet_default_fee_pct ?? 10}
                onChange={(e) => setCfg({ ...cfg, fleet_default_fee_pct: Number(e.target.value) })}
                className="input-dt pr-8"
                data-testid="fee-fleet-default"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-xs pointer-events-none">%</span>
            </div>
          </div>
        </div>

        {/* 3. Partner commission table — Iter 47 */}
        <div className="border-2 border-[#0a0f1e]/20 bg-[#0a0f1e]/5 p-6" data-testid="partner-multipliers-block">
          <div className="flex items-start justify-between mb-4">
            <div>
              <p className="label-caps text-[#0a0f1e] flex items-center gap-2">
                <Percent className="w-3.5 h-3.5 text-[#f59e0b]" /> Tabela de comissões parceiro
              </p>
              <p className="text-xs text-slate-600 mt-1 max-w-2xl">
                Fórmula (Msg 693): <strong>cliente = admin × multiplier</strong>;
                <strong> partner = cliente × pct%</strong>; do resíduo (cliente−partner),
                <strong> 85% motorista + 15% DiTours</strong>.
                Ex: partner=50% → multiplier=2.0 → admin €100 gera cliente €200 (partner €100, motorista €85, DiTours €15).
                Multiplicador ≥ <strong>1 / (1 − pct/100)</strong> para garantir motorista+DiTours ≥ admin.
              </p>
            </div>
            <GoldButton onClick={saveMultipliers} disabled={savingMult} data-testid="save-partner-multipliers">
              {savingMult ? "A guardar…" : "Guardar tabela"}
            </GoldButton>
          </div>
          {multipliers.length === 0 ? (
            <div className="text-sm text-slate-500">A carregar…</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-500">
                    <th className="p-2 text-left">Partner %</th>
                    <th className="p-2 text-left">Multiplicador</th>
                    <th className="p-2 text-left">Ex admin=100€ → cliente</th>
                    <th className="p-2 text-left">Motorista (85% do resíduo)</th>
                    <th className="p-2 text-left">DiTours (15% do resíduo)</th>
                    <th className="p-2 text-left">Partner ganha</th>
                  </tr>
                </thead>
                <tbody>
                  {multipliers.map((row, i) => {
                    // Msg 693: cliente = admin × multiplier; partner = cliente × pct/100;
                    // remaining = cliente − partner; DiTours = 15% × remaining; motorista = 85% × remaining.
                    const admin = 100;
                    const feeRatio = 0.15;
                    const client = admin * (Number(row.multiplier) || 1);
                    const partnerEarn = client * (Number(row.pct) || 0) / 100;
                    const remaining = client - partnerEarn;
                    const ditours = remaining * feeRatio;
                    const motorista = remaining - ditours;
                    const minMul = 1 / (1 - (Number(row.pct) || 0) / 100);
                    const belowMin = Number(row.multiplier) < minMul - 5e-3;
                    return (
                      <tr key={i} className="border-b border-slate-100">
                        <td className="p-2">
                          <div className="flex items-center gap-1">
                            <Input type="number" min="0" step="1" value={row.pct}
                              onChange={(e) => setMultRow(i, "pct", e.target.value)}
                              className="w-20 h-8 text-right"
                              data-testid={`mult-pct-${i}`} />
                            <span className="text-xs text-slate-500">%</span>
                          </div>
                        </td>
                        <td className="p-2">
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-slate-500">×</span>
                            <Input type="number" min="1" step="0.01" value={row.multiplier}
                              onChange={(e) => setMultRow(i, "multiplier", e.target.value)}
                              className={`w-24 h-8 text-right ${belowMin ? "border-red-400 bg-red-50" : ""}`}
                              data-testid={`mult-val-${i}`} />
                          </div>
                          {belowMin && <div className="text-[9px] text-red-600 mt-0.5">Mín {minMul.toFixed(2)}</div>}
                        </td>
                        <td className="p-2 text-sm font-mono text-[#0a0f1e]">€{client.toFixed(2)}</td>
                        <td className="p-2 text-sm font-mono text-slate-600">€{motorista.toFixed(2)}</td>
                        <td className="p-2 text-sm font-mono text-[#f59e0b]">€{ditours.toFixed(2)}</td>
                        <td className="p-2 text-sm font-mono text-emerald-700">€{partnerEarn.toFixed(2)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* 4. IfthenPay passthrough toggle */}
        <div className="border border-slate-200 bg-white p-6" data-testid="fee-ifthenpay-block">
          <div className="flex items-start justify-between mb-4">
            <div>
              <p className="label-caps">IfthenPay · fees de processamento</p>
              <p className="text-xs text-slate-600 mt-1">Pode acrescentar o custo do processamento ao valor pago pelo cliente ou absorver internamente.</p>
            </div>
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!cfg.ifthenpay_passthrough}
                onChange={(e) => setCfg({ ...cfg, ifthenpay_passthrough: e.target.checked })}
                className="accent-[#f59e0b] w-4 h-4"
                data-testid="fee-ifthenpay-passthrough"
              />
              <span className="text-xs uppercase tracking-widest font-bold text-[#0a0f1e]">Acrescentar ao cliente</span>
            </label>
          </div>
          <div className={`grid grid-cols-3 gap-4 transition-opacity ${cfg.ifthenpay_passthrough ? "opacity-100" : "opacity-40 pointer-events-none"}`}>
            <div>
              <label className="label-caps block mb-1.5">MB Way (%)</label>
              <Input type="number" step="0.1" value={cfg.ifthenpay_mbway_pct ?? 0}
                onChange={(e) => setCfg({ ...cfg, ifthenpay_mbway_pct: Number(e.target.value) })}
                className="input-dt" data-testid="fee-ifthenpay-mbway" />
            </div>
            <div>
              <label className="label-caps block mb-1.5">Cartão (%)</label>
              <Input type="number" step="0.1" value={cfg.ifthenpay_card_pct ?? 0}
                onChange={(e) => setCfg({ ...cfg, ifthenpay_card_pct: Number(e.target.value) })}
                className="input-dt" data-testid="fee-ifthenpay-card" />
            </div>
            <div>
              <label className="label-caps block mb-1.5">Taxa fixa (€)</label>
              <Input type="number" step="0.05" value={cfg.ifthenpay_fixed_fee ?? 0}
                onChange={(e) => setCfg({ ...cfg, ifthenpay_fixed_fee: Number(e.target.value) })}
                className="input-dt" data-testid="fee-ifthenpay-fixed" />
            </div>
          </div>
          {!cfg.ifthenpay_passthrough && (
            <div className="mt-4 flex items-start gap-2 text-[11px] text-slate-500">
              <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
              <span>Actualmente a plataforma absorve o custo do processamento. Active o toggle acima para o passar ao cliente.</span>
            </div>
          )}
        </div>

        {/* Advanced collapsible */}
        <div className="border border-slate-200 bg-white">
          <button
            type="button"
            onClick={() => setAdvancedOpen((v) => !v)}
            data-testid="fee-advanced-toggle"
            className="w-full flex items-center justify-between px-6 py-4 hover:bg-slate-50"
          >
            <div className="text-left">
              <div className="label-caps">Configurações avançadas</div>
              <div className="text-xs text-slate-500 mt-1">Buffers de auto-dispatch, política de cancelamento, mínimos.</div>
            </div>
            <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${advancedOpen ? "rotate-180" : ""}`} />
          </button>
          {advancedOpen && (
            <div className="px-6 pb-6 pt-2 space-y-6" data-testid="fee-advanced-panel">
              {ADVANCED_GROUPS.map((g) => (
                <div key={g.title} className="border-t border-slate-100 pt-4">
                  <p className="label-caps mb-3">{g.title}</p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {g.fields.map((f) => (
                      <div key={f.k}>
                        <label className="label-caps block mb-1.5">{f.l}</label>
                        <Input
                          type={f.type}
                          step={f.step}
                          value={cfg[f.k] ?? ""}
                          onChange={(e) => setCfg({ ...cfg, [f.k]: f.type === "number" ? Number(e.target.value) : e.target.value })}
                          className="input-dt"
                          data-testid={`fee-${f.k}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              <div className="border-t border-slate-100 pt-4">
                <p className="label-caps mb-3">Método de pricing</p>
                <div className="flex gap-2 max-w-md">
                  {["higher", "lower"].map((m) => (
                    <button key={m} type="button" onClick={() => setCfg({ ...cfg, pricing_method: m })}
                      data-testid={`method-${m}`}
                      className={`flex-1 py-2.5 text-xs uppercase border ${cfg.pricing_method === m ? "border-[#f59e0b] text-[#f59e0b]" : "border-slate-200 text-slate-500"}`}>
                      Usar {m}
                    </button>
                  ))}
                </div>
                <label className="flex items-center gap-3 cursor-pointer pt-4">
                  <input type="checkbox" checked={cfg.round_to_euro} onChange={(e) => setCfg({ ...cfg, round_to_euro: e.target.checked })} className="accent-[#f59e0b] w-4 h-4" />
                  <span className="text-sm text-slate-700">Arredondar ao euro mais próximo</span>
                </label>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
